import { useState } from 'react';
import { Check } from 'lucide-react';
import { CVUploadSection } from '@/components/custom/CVUploadSection.components';
import { PreInterviewForm } from '@/components/custom/PreInterviewForm.components';
import { InterviewDetailsSection } from '@/components/custom/InterviewDetailsSection.components';
import { ApiTypes } from '@/types/server/api.types';

export const DashboardPage = ({
	setIsUserLoggedIn,
	setUserDetails,
}: {
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data']
	) => void;
}) => {
	const [currentSection, setCurrentSection] = useState<
		'cvUpload' | 'form' | 'interviewDetails'
	>('cvUpload');

	const steps = [
		{ key: 'cvUpload', label: 'Upload CV' },
		{ key: 'form', label: 'Preferences' },
		{ key: 'interviewDetails', label: 'Interview' },
	] as const;

	const currentStepIndex = steps.findIndex(
		(step) => step.key === currentSection
	);

	const goToNext = () => {
		const nextStep = steps[currentStepIndex + 1];
		if (nextStep) setCurrentSection(nextStep.key);
	};

	return (
		<div className="min-h-screen bg-blue-50 flex flex-col items-center py-12 px-4">
			{/* Stepper */}
			<div className="w-full max-w-2xl relative mb-10">
				{/* Step connector lines */}
				<div className="flex justify-between items-center absolute top-4 left-0 right-0 z-0">
					{/* This is a container for connector lines that ensures proper alignment */}
					<div className="w-full flex justify-center px-4">
						{/* Each step has a position */}
						{steps.map((_, index) => (
							<div
								key={`step-position-${index}`}
								className="relative flex-1 flex justify-center"
							>
								{/* We only render connector lines between steps */}
								{index < steps.length - 1 && (
									<div
										className={`absolute h-0.5 w-full left-[calc(50%+4px)] ${
											index < currentStepIndex ? 'bg-blue-500' : 'bg-gray-200'
										}`}
										style={{
											backgroundImage:
												index < currentStepIndex
													? 'linear-gradient(to right, #3b82f6 50%, transparent 50%)'
													: 'linear-gradient(to right, #e5e7eb 50%, transparent 50%)',
											backgroundSize: '8px 1px',
											backgroundRepeat: 'repeat-x',
										}}
									/>
								)}
							</div>
						))}
					</div>
				</div>

				<div className="flex justify-between items-center relative z-10">
					{steps.map((step, index) => {
						const isCompleted = index < currentStepIndex;
						const isActive = index === currentStepIndex;

						return (
							<div
								key={step.key}
								className="flex flex-col items-center text-center"
								style={{ width: `${100 / steps.length}%` }}
							>
								<div
									className={`
										flex items-center justify-center w-8 h-8 rounded-full
										text-sm font-medium mb-2
										transition-all duration-300
										${isCompleted ? 'bg-blue-600 text-white' : ''}
										${isActive ? 'bg-white text-blue-600 border-2 border-blue-600' : ''}
										${
											!isActive && !isCompleted
												? 'bg-gray-100 text-gray-500 border border-gray-300'
												: ''
										}
									`}
								>
									{isCompleted ? <Check className="w-4 h-4" /> : index + 1}
								</div>
								<span
									className={`text-xs font-medium ${
										isActive
											? 'text-blue-600'
											: isCompleted
											? 'text-blue-500'
											: 'text-gray-500'
									}`}
								>
									{step.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>

			{/* Section Content */}
			<div className="w-full max-w-2xl">
				{currentSection === 'cvUpload' && (
					<CVUploadSection
						onNext={goToNext}
						setIsUserLoggedIn={setIsUserLoggedIn}
						setUserDetails={setUserDetails}
					/>
				)}
				{currentSection === 'form' && (
					<PreInterviewForm
						onNext={goToNext}
						setIsUserLoggedIn={setIsUserLoggedIn}
						setUserDetails={setUserDetails}
					/>
				)}
				{currentSection === 'interviewDetails' && (
					<div className="w-full max-w-5xl mx-auto">
						<InterviewDetailsSection
							setIsUserLoggedIn={setIsUserLoggedIn}
							setUserDetails={setUserDetails}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
